import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { ModelViewerElement } from '@google/model-viewer';

import { SHOW_DEBUG } from '../../../../../../../config';
import { generateNanoId } from '../../../../../../../utils/generateNanoId';
import { useCurrentUser } from '../../../../../../../auth/hooks/useAuth';
import { useToastNotification } from '../../../../../../../common/hooks/useToastNotification';
import { modelViewersKeys } from '../../../../../../../locales/keys';
import { useCreateRenderGeneration } from '../../../../../../renderGenerations/hooks/useCreateRenderGeneration';
import { RENDER_GENERATION_POLLING_INTERVAL } from '../../../../../../renderGenerations/renderGenerationsConstants';
import { RenderGenerationsBffUrl } from '../../../../../../renderGenerations/RenderGenerationsBffUrl';
import {
  CreateRenderGenerationInput,
  FetchRenderGenerationResponse,
  RenderGenerationStatus,
  RenderGenerationType
} from '../../../../../../renderGenerations/renderGenerationsTypes';
import { useModelViewerIncomingPayload } from '../useModelViewerIncomingPayload';
import {
  GenerationPollResult,
  useGenerationPolling
} from '../useModelViewerLifestyleGeneration/useGenerationPolling';
import {
  ModelViewerAspectRatioSelection,
  ModelViewerImageSizeValue
} from '../../modelViewerAspectRatioConstants';
import { resolveModelViewerAspectRatio } from '../../utils/resolveModelViewerAspectRatio';

interface UseModelViewerRenderGenerationOptions {
  modelViewerEl: ModelViewerElement | null;
  modelId: string | null;
  setActiveViewId: Dispatch<SetStateAction<string>>;
  setIsAiModeActive: Dispatch<SetStateAction<boolean>>;
  // Sent verbatim as aspect_ratio/image_size — the backend resolves the
  // output resolution from this pair. requested_width/requested_height are
  // still measured from the (ratio-shaped) viewer box at request time, for
  // backward compatibility only.
  // May be "auto": resolved to the nearest preset from the live viewer box at
  // request time, so the outgoing request never carries "auto".
  aspectRatio: ModelViewerAspectRatioSelection;
  imageSize: ModelViewerImageSizeValue;
}

export interface RenderGenerationItem {
  kind: 'render';
  createdOrder: number;
  localId: string;
  serverId?: string;
  status: RenderGenerationStatus;
  resultPreviewUrl?: string;
  resultFullUrl?: string;
  errorMessage?: string;
}

function mapRenderGenerationPollResponse(
  data: FetchRenderGenerationResponse
): GenerationPollResult<RenderGenerationStatus> {
  const result = data.results[0];

  if (data.status === RenderGenerationStatus.Completed) {
    return {
      status: RenderGenerationStatus.Completed,
      resultPreviewUrl: result?.image_result,
      resultFullUrl: result?.image_result,
      isSuccess: true
    };
  }

  if (
    data.status === RenderGenerationStatus.Failed ||
    data.status === RenderGenerationStatus.Cancelled
  ) {
    return {
      status: RenderGenerationStatus.Failed,
      errorMessage: data.error_message ?? result?.error_message ?? undefined,
      isFailed: true
    };
  }

  return { status: data.status };
}

function getRenderGenerationErrorMessage(err: unknown): string {
  const error = err as { fullMessages?: string[]; message?: string };
  return error.fullMessages?.[0] || error.message || 'Failed to request render';
}

function useModelViewerRenderGeneration({
  modelViewerEl,
  modelId,
  setActiveViewId,
  setIsAiModeActive,
  aspectRatio,
  imageSize
}: UseModelViewerRenderGenerationOptions) {
  const { createRenderGeneration } = useCreateRenderGeneration();
  const currentUser = useCurrentUser();
  const { buildIncomingPayload } = useModelViewerIncomingPayload({
    modelViewerEl
  });
  const [isRequesting, setIsRequesting] = useState(false);
  const [renderGenerations, setRenderGenerations] = useState<
    RenderGenerationItem[]
  >([]);

  const { showToastI18nNotification: showSuccessToast } = useToastNotification({
    appearance: 'success'
  });
  const { showToastI18nNotification: showErrorToast } = useToastNotification({
    appearance: 'error'
  });

  const handlePollTick = useCallback(
    (localId: string, result: GenerationPollResult<RenderGenerationStatus>) => {
      const generationResult = {
        status: result.status,
        resultPreviewUrl: result.resultPreviewUrl,
        resultFullUrl: result.resultFullUrl,
        errorMessage: result.errorMessage
      };

      setRenderGenerations((prev) =>
        prev.map((g) => {
          if (g.localId !== localId) return g;
          if (
            g.status === generationResult.status &&
            generationResult.status !== RenderGenerationStatus.Completed &&
            generationResult.status !== RenderGenerationStatus.Failed
          ) {
            return g;
          }
          return { ...g, ...generationResult };
        })
      );
    },
    []
  );

  const { startPolling, stopPolling } = useGenerationPolling({
    buildShowUrl: RenderGenerationsBffUrl.show,
    mapResponse: mapRenderGenerationPollResponse,
    intervalMs: RENDER_GENERATION_POLLING_INTERVAL,
    onTick: handlePollTick,
    onSuccess: setActiveViewId
  });

  const handleRequestRender = useCallback(async () => {
    if (isRequesting || !modelId || !modelViewerEl) return;

    const localId = generateNanoId();
    setIsAiModeActive(true);
    setRenderGenerations((prev) => [
      {
        kind: 'render',
        createdOrder: Date.now(),
        localId,
        status: RenderGenerationStatus.Pending
      },
      ...prev
    ]);
    setActiveViewId(localId);
    setIsRequesting(true);

    try {
      // viewerRect already reflects the aspect-ratio frame (or the full box in
      // auto mode), so the legacy requested_width/requested_height pair and the
      // auto resolution both come from the same measurement.
      const viewerRect = modelViewerEl.getBoundingClientRect();
      const resolvedAspectRatio = resolveModelViewerAspectRatio(
        aspectRatio,
        viewerRect
      );
      const incomingPayload = buildIncomingPayload(resolvedAspectRatio);

      if (SHOW_DEBUG && incomingPayload) {
        const { camera } = incomingPayload;
        const dx = camera.world_position.x - camera.look_at.x;
        const dy = camera.world_position.y - camera.look_at.y;
        const dz = camera.world_position.z - camera.look_at.z;
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
        // Sanity invariants: |world_position - look_at| should ≈ orbit.radius,
        // and forward should ≈ normalize(look_at - world_position).
        console.debug('[render generation] payload', incomingPayload);
        console.debug('[render generation] invariants', {
          cameraToLookAtDistance: +distance.toFixed(4),
          orbitRadius: camera.orbit.radius,
          forward: camera.forward
        });
      }

      const width = viewerRect ? Math.round(viewerRect.width) : undefined;
      const height = viewerRect ? Math.round(viewerRect.height) : undefined;

      const input: CreateRenderGenerationInput = {
        generation_type: RenderGenerationType.SiloStudio,
        model_id: modelId,
        user_id: currentUser?.id ?? null,
        requested_width: width,
        requested_height: height,
        aspect_ratio: resolvedAspectRatio,
        image_size: imageSize,
        incoming_payload: incomingPayload
      };

      const response = await createRenderGeneration(input);

      setRenderGenerations((prev) =>
        prev.map((g) =>
          g.localId === localId ? { ...g, serverId: response.id } : g
        )
      );
      startPolling(localId, response.id);

      showSuccessToast(modelViewersKeys.renderRequested);
    } catch (err) {
      const message = getRenderGenerationErrorMessage(err);
      showErrorToast(modelViewersKeys.renderRequestFailed);
      setRenderGenerations((prev) =>
        prev.map((g) =>
          g.localId === localId
            ? {
                ...g,
                status: RenderGenerationStatus.Failed,
                errorMessage: message
              }
            : g
        )
      );
      console.error('[render generation]', err);
    } finally {
      setIsRequesting(false);
    }
  }, [
    isRequesting,
    modelId,
    modelViewerEl,
    currentUser?.id,
    buildIncomingPayload,
    createRenderGeneration,
    startPolling,
    showSuccessToast,
    showErrorToast,
    setActiveViewId,
    setIsAiModeActive,
    aspectRatio,
    imageSize
  ]);

  const dismissRenderGeneration = useCallback(
    (localId: string) => {
      stopPolling(localId);
      setRenderGenerations((prev) => prev.filter((g) => g.localId !== localId));
      setActiveViewId((prev) => (prev === localId ? 'model' : prev));
    },
    [setActiveViewId, stopPolling]
  );

  const handleRemoveRenderGeneration = dismissRenderGeneration;
  const handleStopRenderGeneration = dismissRenderGeneration;

  return {
    renderGenerations,
    isRequesting,
    handleRequestRender,
    handleRemoveRenderGeneration,
    handleStopRenderGeneration
  };
}

export default useModelViewerRenderGeneration;
